import React, { createContext, useContext, useReducer, useEffect } from "react";

/**
 * AI Calendar Wizard State Management
 * Multi-step wizard for bulk content planning and generation
 */

const AICalendarContext = createContext(null);

// Storage key for state persistence
const STORAGE_KEY = "aiCalendarWizardState";
const STORAGE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

// Initial state
const initialState = {
  currentStep: 1,
  strategy: null,
  topics: [],
  schedule: [],
  generatedPosts: [],
  jobId: null,
  businessContext: null,
  selectedAccounts: [],
  // The same accounts as `selectedAccounts`, but with their platform kept —
  // the generator returns slots without an account id, so finalize needs this
  // to map each post back to a real account (OP#2494).
  selectedAccountDetails: [],
  timeRange: null,
  error: null,
  timestamp: Date.now(),
};

// Action types
const ACTIONS = {
  SET_STEP: "SET_STEP",
  SET_STRATEGY: "SET_STRATEGY",
  SET_TOPICS: "SET_TOPICS",
  SET_SCHEDULE: "SET_SCHEDULE",
  SET_GENERATED_POSTS: "SET_GENERATED_POSTS",
  SET_JOB_ID: "SET_JOB_ID",
  SET_BUSINESS_CONTEXT: "SET_BUSINESS_CONTEXT",
  SET_SELECTED_ACCOUNTS: "SET_SELECTED_ACCOUNTS",
  SET_SELECTED_ACCOUNT_DETAILS: "SET_SELECTED_ACCOUNT_DETAILS",
  SET_TIME_RANGE: "SET_TIME_RANGE",
  SET_ERROR: "SET_ERROR",
  NEXT_STEP: "NEXT_STEP",
  PREV_STEP: "PREV_STEP",
  RESET: "RESET",
  RESTORE_STATE: "RESTORE_STATE",
};

// Reducer
function aiCalendarReducer(state, action) {
  let newState = state;

  switch (action.type) {
    case ACTIONS.SET_STEP:
      newState = { ...state, currentStep: action.payload, error: null };
      break;

    case ACTIONS.NEXT_STEP:
      newState = {
        ...state,
        currentStep: Math.min(state.currentStep + 1, 6),
        error: null,
      };
      break;

    case ACTIONS.PREV_STEP:
      newState = {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 1),
        error: null,
      };
      break;

    case ACTIONS.SET_STRATEGY:
      newState = { ...state, strategy: action.payload, error: null };
      break;

    case ACTIONS.SET_TOPICS:
      newState = { ...state, topics: action.payload, error: null };
      break;

    case ACTIONS.SET_SCHEDULE:
      newState = { ...state, schedule: action.payload, error: null };
      break;

    case ACTIONS.SET_GENERATED_POSTS:
      newState = { ...state, generatedPosts: action.payload, error: null };
      break;

    case ACTIONS.SET_JOB_ID:
      newState = { ...state, jobId: action.payload };
      break;

    case ACTIONS.SET_BUSINESS_CONTEXT:
      newState = { ...state, businessContext: action.payload };
      break;

    case ACTIONS.SET_SELECTED_ACCOUNTS:
      newState = { ...state, selectedAccounts: action.payload };
      break;

    case ACTIONS.SET_SELECTED_ACCOUNT_DETAILS:
      newState = { ...state, selectedAccountDetails: action.payload };
      break;

    case ACTIONS.SET_TIME_RANGE:
      newState = { ...state, timeRange: action.payload };
      break;

    case ACTIONS.SET_ERROR:
      newState = { ...state, error: action.payload };
      break;

    case ACTIONS.RESET:
      newState = { ...initialState, timestamp: Date.now() };
      break;

    case ACTIONS.RESTORE_STATE:
      newState = { ...action.payload, timestamp: Date.now() };
      break;

    default:
      return state;
  }

  // Update timestamp on any state change (except restore)
  if (action.type !== ACTIONS.RESTORE_STATE) {
    newState = { ...newState, timestamp: Date.now() };
  }

  // Persist state to sessionStorage
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  } catch (error) {
    console.error("Failed to persist wizard state:", error);
  }

  return newState;
}

// Load state from sessionStorage
function loadPersistedState() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    const age = Date.now() - (parsed.timestamp || 0);

    // Expire after 1 hour
    if (age > STORAGE_EXPIRY) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Failed to load persisted wizard state:", error);
    return null;
  }
}

/**
 * AI Calendar Context Provider
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export function AICalendarProvider({ children }) {
  const [state, dispatch] = useReducer(aiCalendarReducer, initialState);

  // Restore state on mount
  useEffect(() => {
    const persisted = loadPersistedState();
    if (persisted) {
      dispatch({ type: ACTIONS.RESTORE_STATE, payload: persisted });
    }
  }, []);

  const value = {
    // State
    state,

    // Actions
    setStep: (step) => dispatch({ type: ACTIONS.SET_STEP, payload: step }),
    nextStep: () => dispatch({ type: ACTIONS.NEXT_STEP }),
    prevStep: () => dispatch({ type: ACTIONS.PREV_STEP }),
    setStrategy: (strategy) =>
      dispatch({ type: ACTIONS.SET_STRATEGY, payload: strategy }),
    setTopics: (topics) =>
      dispatch({ type: ACTIONS.SET_TOPICS, payload: topics }),
    setSchedule: (schedule) =>
      dispatch({ type: ACTIONS.SET_SCHEDULE, payload: schedule }),
    setGeneratedPosts: (posts) =>
      dispatch({ type: ACTIONS.SET_GENERATED_POSTS, payload: posts }),
    setJobId: (jobId) => dispatch({ type: ACTIONS.SET_JOB_ID, payload: jobId }),
    setBusinessContext: (context) =>
      dispatch({ type: ACTIONS.SET_BUSINESS_CONTEXT, payload: context }),
    setSelectedAccounts: (accounts) =>
      dispatch({ type: ACTIONS.SET_SELECTED_ACCOUNTS, payload: accounts }),
    setSelectedAccountDetails: (accounts) =>
      dispatch({ type: ACTIONS.SET_SELECTED_ACCOUNT_DETAILS, payload: accounts }),
    setTimeRange: (range) =>
      dispatch({ type: ACTIONS.SET_TIME_RANGE, payload: range }),
    setError: (error) => dispatch({ type: ACTIONS.SET_ERROR, payload: error }),
    reset: () => {
      sessionStorage.removeItem(STORAGE_KEY);
      dispatch({ type: ACTIONS.RESET });
    },
  };

  return (
    <AICalendarContext.Provider value={value}>
      {children}
    </AICalendarContext.Provider>
  );
}

/**
 * Hook to use AI Calendar context
 * @returns {Object} AI Calendar context value with state and actions
 */
export function useAICalendar() {
  const context = useContext(AICalendarContext);
  if (!context) {
    throw new Error("useAICalendar must be used within AICalendarProvider");
  }
  return context;
}

export default AICalendarContext;
